import {
  createSlice,
  PayloadAction,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import { fetchUserIp, getStrings, getTsAffiliates, getTsAffiliatesClicks, getTsInitialData } from "./AppApi";
import { getPlaceAutosuggest, pollFlightSearch, flightSearch } from "../flightsSlice/FlightsApi";
import { saveTsClicksApi, saveTsFlightsApi, checkTsEligibilityApi, extendTsClickTimerApi } from "../userSlice/UserApi";
import heTranslation from "../../../locales/he/translation.json";
import enTranslation from "../../../locales/en/translation.json";
import i18n from "../../../i18n/config";
import { API_RESULT } from "@pfs/react-ui-kit/utils/enums";
import {
  Affiliate,
  AffiliateClicks,
  AffiliateAggregatorTimer,
  TsInitialDataResponse,
} from "../../../utils/types";
import { feAffiliateIdFromGetAffiliatesResponse } from "../../../utils/feAffiliateImpact";

interface AppState {
  sessionId?: string;
  accessToken?: string;
  refreshToken?: string;
  /** Analytics user identifier from validate-otp response (distinct from JWT accessToken). */
  userToken?: string;
  /** Encrypted customer id from validate-otp; sent as `general.customer_id_encrypted` on analytics events. */
  customerIdEncrypted?: string;
  /** Last animated total (UICurrency); -1 = unset. Not persisted. */
  previousTotalAmount: number;
  strings: Record<string, string>; // Translations/strings from server merged with static translations (always initialized)
  showStringKeys?: boolean; // Show translation keys in development
  tsInitialData?: TsInitialDataResponse | null; // TS initial data from server
  tsInitialDateStatus?: API_RESULT;
  loadingCount: number; // Counter for pending API calls
  affiliates: Affiliate[] | null;
  affiliatesClicks: AffiliateClicks[] | null; // Timer per website from getTsAffiliatesClicks (affiliates)
  /** Hero / flight banner timers from get-affiliates-timers (`affiliatesTimers`). */
  affiliatesTimers: AffiliateAggregatorTimer[] | null;
  /** Epoch ms when `affiliatesTimers` was last written (used by useAffiliateHeroTimer to compute elapsed time). */
  affiliatesTimersReceivedAt: number | null;
  tsAffiliatesStatus?: API_RESULT;
  /** IMPACT channel id from last get-affiliates (same as flights-screen save-ts-click). */
  impactFeAffiliateId: number;
  /** Feature flag: when true the CreditGuard CVV step is shown during login. */
  showCvvStep: boolean;
  /** Feature flag: when true, flexible-dates calendar shows selectable months; otherwise shows "Coming soon..". */
  showFlexibleDatesMonths: boolean;
  /** Public IP address of the user, fetched once and persisted. */
  userIp: string;
}

// Get static translations based on current language
// Note: Only flat keys (starting with 't_') are used, nested objects are ignored
const getStaticTranslations = (): Record<string, string> => {
  try {
    const currentLang = i18n.language || "he";
    const staticTranslations =
      currentLang === "he" ? heTranslation : enTranslation;

    // Extract only flat string keys (not nested objects)
    const flattened: Record<string, string> = {};

    const flattenObject = (obj: any, prefix = "") => {
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          if (typeof value === "string") {
            // Only include keys that start with 't_' (translation keys)
            const finalKey = prefix ? `${prefix}_${key}` : key;
            if (finalKey.startsWith("t_")) {
              flattened[finalKey] = value;
            }
          } else if (typeof value === "object" && value !== null) {
            // Recursively flatten nested objects
            flattenObject(value, prefix ? `${prefix}_${key}` : key);
          }
        }
      }
    };

    flattenObject(staticTranslations);
    return flattened;
  } catch (error) {
    console.warn(
      "Failed to load static translations, using empty object",
      error
    );
    return {};
  }
};

const initialState: AppState = {
  strings: getStaticTranslations(), // Initialize with static translations
  previousTotalAmount: -1,
  showStringKeys: false,
  tsInitialData: null,
  loadingCount: 0,
  affiliates: [],
  affiliatesClicks: null,
  affiliatesTimers: null,
  affiliatesTimersReceivedAt: null,
  tsAffiliatesStatus: API_RESULT.IDLE,
  impactFeAffiliateId: 0,
  showCvvStep: false,
  showFlexibleDatesMonths: false,
  userIp: "",
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setHeaderToken: (
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken?: string;
        userToken?: string;
        customerIdEncrypted?: string;
      }>
    ) => {
      state.accessToken = action.payload.accessToken;
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
      }
      if (action.payload.userToken) {
        state.userToken = action.payload.userToken;
      }
      if (action.payload.customerIdEncrypted) {
        state.customerIdEncrypted = action.payload.customerIdEncrypted;
      }
    },
    setCustomerIdEncrypted: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload !== undefined) {
        state.customerIdEncrypted = action.payload;
      }
    },
    clearTokens: (state) => {
      state.accessToken = undefined;
      state.refreshToken = undefined;
      state.userToken = undefined;
      state.customerIdEncrypted = undefined;
      state.sessionId = undefined;
      state.impactFeAffiliateId = 0;
    },
    setPreviousTotalAmount: (state, action: PayloadAction<number>) => {
      state.previousTotalAmount = action.payload;
    },
    setShowCvvStep: (state, action: PayloadAction<boolean>) => {
      state.showCvvStep = action.payload;
    },
    setShowFlexibleDatesMonths: (state, action: PayloadAction<boolean>) => {
      state.showFlexibleDatesMonths = action.payload;
    },
    /** Manual counter bump for GlobalLoader when checkTsEligibilityApi is excluded from matchers (e.g. post-login flow in Login). Pair with decrementLoadingCount in finally. */
    incrementLoadingCount: (state) => {
      state.loadingCount += 1;
    },
    decrementLoadingCount: (state) => {
      state.loadingCount = Math.max(0, state.loadingCount - 1);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStrings.pending, (state) => {
        // Handle pending state if needed
      })
      .addCase(getStrings.fulfilled, (state, action) => {
        // Merge server strings with static translations
        // Server strings override static translations if they exist
        if (action.payload?.responseData?.strings) {
          // Get current static translations to ensure they're always included
          const staticTranslations = getStaticTranslations();
          state.strings = {
            ...staticTranslations, // Base: static translations
            ...state.strings, // Keep any existing strings
            ...action.payload.responseData.strings, // Server strings override everything
          };
        }
      })
      .addCase(getStrings.rejected, (state, action) => {
        // Handle rejected state if needed
      })
      .addCase(getTsInitialData.pending, (state) => {
        state.tsInitialDateStatus = API_RESULT.LOADING;
      })
      .addCase(getTsInitialData.fulfilled, (state, action) => {
        state.tsInitialData = action.payload;
        state.tsInitialDateStatus = API_RESULT.SUCCEEDED;
        state.showCvvStep = action.payload?.tsValidateCVV === true;
      })
      .addCase(getTsInitialData.rejected, (state, action) => {
        // Handle rejected state if needed
        state.tsInitialDateStatus = API_RESULT.FAILED;
      })
      .addCase(getTsAffiliates.pending, (state) => {
        state.tsAffiliatesStatus = API_RESULT.LOADING;
      })
      .addCase(getTsAffiliates.fulfilled, (state, action) => {
        state.affiliates = action.payload.affiliates;
        state.impactFeAffiliateId = feAffiliateIdFromGetAffiliatesResponse(
          action.payload
        );
        state.tsAffiliatesStatus = API_RESULT.SUCCEEDED;
      })
      .addCase(getTsAffiliates.rejected, (state, action) => {
        state.tsAffiliatesStatus = API_RESULT.FAILED;
      })
      .addCase(getTsAffiliatesClicks.pending, (state) => {
        state.tsAffiliatesStatus = API_RESULT.LOADING;
      })
      .addCase(getTsAffiliatesClicks.fulfilled, (state, action) => {
        const payload = action.payload;
        state.affiliatesTimers = payload?.affiliatesTimers ?? null;
        state.affiliatesTimersReceivedAt = Date.now();
        const affiliatesFromClicks = payload?.affiliates;
        if (affiliatesFromClicks != null) {
          state.affiliatesClicks = affiliatesFromClicks;
          state.affiliates = affiliatesFromClicks.map((a) => ({
            type: a.type,
            websites: a.websites.map(({ affiliateTimer: _t, ...w }) => w),
          }));
        }
        state.tsAffiliatesStatus = API_RESULT.SUCCEEDED;
      })
      .addCase(getTsAffiliatesClicks.rejected, (state) => {
        state.tsAffiliatesStatus = API_RESULT.FAILED;
      })
      .addCase(extendTsClickTimerApi.fulfilled, (state, action) => {
        const payload = action.payload;
        if (payload.affiliatesTimers) {
          state.affiliatesTimers = payload.affiliatesTimers;
          state.affiliatesTimersReceivedAt = Date.now();
        }
        if (payload.affiliates) {
          state.affiliatesClicks = payload.affiliates;
          state.affiliates = payload.affiliates.map((a) => ({
            type: a.type,
            websites: a.websites.map(({ affiliateTimer: _t, ...w }) => w),
          }));
        }
        const feTimer = payload.feTimer;
        const feAffiliateId = String(action.meta.arg.feAffiliateId);
        if (
          feTimer != null &&
          feTimer >= 0 &&
          state.affiliatesClicks?.length
        ) {
          state.affiliatesClicks = state.affiliatesClicks.map((affiliate) => ({
            ...affiliate,
            websites: affiliate.websites.map((w) =>
              String(w.id) === feAffiliateId
                ? { ...w, affiliateTimer: feTimer }
                : w,
            ),
          }));
        }
      })
      .addCase(fetchUserIp.fulfilled, (state, action) => {
        state.userIp = action.payload;
      })
      // Track all pending/fulfilled/rejected async thunks for global loading state
      // Use a counter to handle concurrent API calls correctly
      // getTsAffiliatesClicks (get-affiliates-timers) does not show loader
      // getTsAffiliates (qry/settings/get-affiliates) — background fetch, no global loader
      // getPlaceAutosuggest shows spinner in input instead
      // flightSearch (cmd/flights/search) — skeleton shown in FlightSearchResults instead
      // pollFlightSearch (cmd/flights/poll-search) — background polling, no global loader
      // saveTsClicksApi (cmd/clicks/save-ts-click) — silent background save, no global loader
      // extendTsClickTimerApi (cmd/clicks/extend-ts-click-timer) — silent background extend, no global loader
      // saveTsFlightsApi (cmd/flights/save-ts-flight) — local button spinner only; avoid full-screen loader + mis-clicks
      // checkTsEligibilityApi (qry/eligibility/check) — silent background check, no global loader
      .addMatcher(isPending, (state, action) => {
        if (
          action.type !== getTsAffiliatesClicks.pending.type &&
          action.type !== getTsAffiliates.pending.type &&
          action.type !== getPlaceAutosuggest.pending.type &&
          action.type !== flightSearch.pending.type &&
          action.type !== pollFlightSearch.pending.type &&
          action.type !== saveTsClicksApi.pending.type &&
          action.type !== extendTsClickTimerApi.pending.type &&
          action.type !== saveTsFlightsApi.pending.type &&
          action.type !== fetchUserIp.pending.type &&
          action.type !== checkTsEligibilityApi.pending.type
        ) {
          state.loadingCount += 1;
        }
      })
      .addMatcher(isFulfilled, (state, action) => {
        if (
          action.type !== getTsAffiliatesClicks.fulfilled.type &&
          action.type !== getTsAffiliates.fulfilled.type &&
          action.type !== getPlaceAutosuggest.fulfilled.type &&
          action.type !== flightSearch.fulfilled.type &&
          action.type !== pollFlightSearch.fulfilled.type &&
          action.type !== saveTsClicksApi.fulfilled.type &&
          action.type !== extendTsClickTimerApi.fulfilled.type &&
          action.type !== saveTsFlightsApi.fulfilled.type &&
          action.type !== fetchUserIp.fulfilled.type &&
          action.type !== checkTsEligibilityApi.fulfilled.type
        ) {
          state.loadingCount = Math.max(0, state.loadingCount - 1);
        }
      })
      .addMatcher(isRejected, (state, action) => {
        if (
          action.type !== getTsAffiliatesClicks.rejected.type &&
          action.type !== getTsAffiliates.rejected.type &&
          action.type !== getPlaceAutosuggest.rejected.type &&
          action.type !== flightSearch.rejected.type &&
          action.type !== pollFlightSearch.rejected.type &&
          action.type !== saveTsClicksApi.rejected.type &&
          action.type !== extendTsClickTimerApi.rejected.type &&
          action.type !== saveTsFlightsApi.rejected.type &&
          action.type !== fetchUserIp.rejected.type &&
          action.type !== checkTsEligibilityApi.rejected.type
        ) {
          state.loadingCount = Math.max(0, state.loadingCount - 1);
        }
      });
  },
});

export const {
  setSessionId,
  setHeaderToken,
  setCustomerIdEncrypted,
  clearTokens,
  setPreviousTotalAmount,
  setShowCvvStep,
  setShowFlexibleDatesMonths,
  incrementLoadingCount,
  decrementLoadingCount,
} = appSlice.actions;

export default appSlice.reducer;
